/**
 * Console navigation registry — the plan's pages (§3 / I4), in one table.
 *
 * This is the single source for the sidebar, the router and the breadcrumb, so
 * a page cannot appear in the nav without a route (or an explicit "pending"
 * state). `status` is honest:
 *   live    — renders from the data provider today
 *   pending — route shell only; `pendingReason` says why
 * `planPage` / `apiRefs` come straight from the plan table.
 *
 * `capability` replaces the old three-role field (U-08-f / plan D10,
 * `docs/RBAC_AND_CONSOLE_FA.md` §۱۰): a page
 * declares the ONE §۱ registry key it is opened by, and the LEVEL of that key for the caller is
 * read from `GET /ops/v1/permissions/effective` — never derived here. Nothing in this file decides
 * access; it only names the key a page asks about. `test/navigationCapabilities.test.ts` is what
 * keeps every value below inside the registry the server publishes.
 */

export type PageStatus = 'live' | 'pending';
export type PendingReason = 'api' | 'ui';

export interface NavPage {
  /** Route path relative to the router base. */
  path: string;
  /** Stable id used by nav keys and tests. */
  id: string;
  /** Persian page title (plan §3 wording). */
  title: string;
  /** Short label for the breadcrumb / collapsed nav. */
  crumb: string;
  group: NavGroup;
  icon: IconName;
  /**
   * The page's main capability: one key of `acs_service/identity.py::CAPABILITIES` (§۹ «هر صفحه
   * یک capability اصلی دارد»). Write controls inside the page name their own key; this one is what
   * opens the page.
   */
  capability: string;
  status: PageStatus;
  pendingReason?: PendingReason;
  /** What the plan says this page does — shown on the shell itself. */
  planPage: number;
  /** Endpoints this page needs, with the coverage state from API_COVERAGE_FA.md. */
  apiRefs: readonly string[];
  /** Why the page is pending, in one Persian sentence. */
  pendingNote?: string;
  /** Sub-route (not a sidebar entry) — e.g. device detail. */
  hidden?: boolean;
}

export type NavGroup = 'monitoring' | 'devices' | 'operations' | 'infrastructure' | 'admin';

export const NAV_GROUPS: readonly { id: NavGroup; label: string }[] = [
  { id: 'monitoring', label: 'پایش' },
  { id: 'devices', label: 'دستگاه‌ها' },
  { id: 'operations', label: 'عملیات' },
  { id: 'infrastructure', label: 'زیرساخت' },
  { id: 'admin', label: 'مدیریت' },
];

export type IconName =
  | 'dashboard'
  | 'inventory'
  | 'device'
  | 'activation'
  | 'template'
  | 'firmware'
  | 'diagnostics'
  | 'alarm'
  | 'olt'
  | 'reconciliation'
  | 'settings'
  | 'external'
  | 'search'
  | 'close'
  | 'chevron-right'
  | 'chevron-left'
  | 'chevron-down'
  | 'refresh'
  | 'download'
  | 'power'
  | 'warning'
  | 'info'
  | 'clock'
  | 'wifi'
  | 'link'
  | 'key'
  | 'shield'
  | 'sparkline'
  | 'theme';

const PENDING_PAGES: readonly NavPage[] = [
  {
    id: 'firmware',
    path: 'firmware-campaigns',
    title: 'فیرمویر و کمپین‌ها',
    crumb: 'فیرمویر و کمپین',
    group: 'operations',
    icon: 'firmware',
    capability: 'firmware.read',
    status: 'live',
    planPage: 6,
    apiRefs: [
      'POST/GET /ops/v1/firmware ✅',
      'DELETE /ops/v1/firmware/{file_id} ✅',
      'POST /ops/v1/firmware:upload ✅ E1 (ایمیج بزرگ، بدنه‌ی خام)',
      'POST /ops/v1/campaigns ✅ E4 (موج ۰ را زمان‌بندی می‌کند؛ dispatch در worker)',
      'GET /ops/v1/campaigns/{id} ✅ E3 (`next_action` دلیلِ حرکت‌نکردن موج را می‌گوید)',
      'POST …/campaigns/{id}:halt|:resume|:rollback ✅ E8 (هر شش در نوشتن در کنسول)',
    ],
  },
];

const LIVE_PAGES: readonly NavPage[] = [
  // W5c (2026-11-04). The system-maintenance family on ten EXISTING doors, zero new
  // (`docs/plans/2026-11-04-w5c-maintenance-console.md`). The shell key is `ops.outbox.read`
  // (MW-1): the ONLY family key admin/operator hold — the four owner-only panels gate on
  // their own keys inside, so a role never sees a page their law allows and then finds it
  // empty. `planPage` 20 is the W order's own number: the standard's §2 table has no row 20
  // yet, the same way `keys`/`profile` carry 13 without a numbered row. The GROUP is §۲'s
  // longest-prefix rule, not taste: `ops.outbox.` lands in زیرساخت (beside jobs/reconciliation)
  // — the navigationGroups guard said so and the standard outranks the plan's MW-1 guess.
  {
    id: 'maintenance',
    path: 'maintenance',
    title: 'نگهداری سامانه',
    crumb: 'نگهداری',
    group: 'infrastructure',
    icon: 'refresh',
    capability: 'ops.outbox.read',
    status: 'live',
    planPage: 20,
    apiRefs: [
      'GET /ops/v1/outbox ✅ (صف تحویل با فیلتر وضعیت؛ DLQ با last_error)',
      'POST /ops/v1/outbox/{event_id}:redrive ✅ (فقط ردیف failed؛ کلید Idempotency الزامی)',
      'GET /ops/v1/migrations ✅ (وضعیت schema؛ recorded: null حقیقتِ store است نه «نسخهٔ صفر»)',
      'POST /ops/v1/admin/migrations:apply ✅ (چهار رفض 409 همان‌طور که می‌رسند)',
      'GET /ops/v1/admin/backups ✅ (dump + mongodump؛ پوشهٔ غایب 409 است نه لیست خالی)',
      'POST /ops/v1/admin/backups/{backup_id}:verify ✅ (سه‌حالتیِ intact؛ عمداً بی‌کلید)',
      'POST /ops/v1/admin/retention:run ✅ (informs_held جداست از صفرِ تمیز — U-17)',
      'GET /ops/v1/legal-holds ✅ (فیلتر وضعیت؛ پیش‌فرض active)',
      'POST /ops/v1/legal-holds ✅ (reason اجباری؛ فقط انسانی)',
      'POST /ops/v1/legal-holds/{hold_id}:lift ✅ (دقیقاً یک‌بار؛ 409 همان‌طور که می‌رسد)',
    ],
  },

  {
    id: 'reconciliation',
    path: 'reconciliation',
    title: 'مغایرت‌ها (Reconciliation)',
    crumb: 'مغایرت‌ها',
    group: 'infrastructure',
    icon: 'reconciliation',
    capability: 'reconciliation.read',
    status: 'live',
    planPage: 10,
    apiRefs: [
      'GET /ops/v1/reconciliation ✅ (فهرست گزارش‌ها؛ C6 هنوز worker ندارد)',
      'اقدام re-authorize/unbind 📅 وقتی درهایشان ساخته شوند',
    ],
  },

  {
    id: 'templates',
    path: 'templates',
    title: 'قالب‌ها و سیاست‌ها',
    crumb: 'قالب‌ها',
    group: 'operations',
    icon: 'template',
    capability: 'templates.read',
    status: 'live',
    planPage: 5,
    apiRefs: [
      'GET /ops/v1/templates ✅ (passthrough فهرست presets)',
      'PUT /ops/v1/templates/{name} ✅ E1 (G-PRV-05؛ Idempotency-Key با سندِ کامل)',
    ],
  },

  {
    id: 'dashboard',
    path: '',
    title: 'داشبورد عملیاتی',
    crumb: 'داشبورد',
    group: 'monitoring',
    icon: 'dashboard',
    capability: 'health.read',
    status: 'live',
    planPage: 1,
    apiRefs: [
      'GET /integration/v1/devices ✅',
      'GET /integration/v1/events ✅',
      'GET /integration/v1/health ✅',
    ],
  },
  {
    // ردیفِ ۱۴ از جدولِ §۲ِ استاندارد (`docs/CONSOLE_UI_STANDARD_FA.md`): «سلامت اجزا | پایش |
    // Report | health.read | ✖ (API هست)» — یعنی از روزِ اول همان را می‌گفت که این قلم بست: درها
    // بودند و صفحه نبود. O4 دو درِ اول را منتشر کرد، DA-09 سومی را، و TLM-4b هر سه را روی یک صفحه
    // نشاند. شماره از جدولِ استاندارد است، نه از شمارشِ قلم‌های این فهرست.
    id: 'system',
    path: 'system',
    title: 'سلامت اجزا',
    crumb: 'سلامت اجزا',
    group: 'monitoring',
    icon: 'power',
    capability: 'health.read',
    status: 'live',
    planPage: 14,
    apiRefs: [
      'GET /ops/v1/system/components ✅ E1 (O4 — همان چهار جزو، با شاهدِ زیرِ هر کدام)',
      'GET /ops/v1/system/queues ✅ E1 (O4 — شمارشِ store: واژهٔ بی‌سطر صفرِ شمرده‌شده است، نه غایب)',
      'GET /ops/v1/system/workers ✅ E1 (DA-09 — ضربان را خودِ حلقه می‌نویسد؛ بی‌ردیف یعنی «نادیده‌ام»، نه «مرده»)',
    ],
  },
  {
    // W1 (2026-11-04). Page ۱۷ of the standard's §۲ table: the bulk form of data the caller could
    // already page through. `reports.` sits in پایش by the placement rule's longest prefix, and the
    // page asks for the module key only — each report's SOURCE key is enforced by the server and the
    // catalogue already omits any report the caller could not run.
    id: 'reports',
    path: 'reports',
    title: 'گزارش‌ها',
    crumb: 'گزارش‌ها',
    group: 'monitoring',
    icon: 'download',
    capability: 'reports.read',
    status: 'live',
    planPage: 17,
    apiRefs: [
      'GET /ops/v1/reports ✅ E1 (W1 — کاتالوگ فقط گزارش‌هایی که فراخوان می‌تواند اجرا کند)',
      'GET /ops/v1/reports/{report_id} ✅ E1 (W1 — json | csv | xlsx؛ سقف ردیف و ممیزی خروجی؛ چاپ = چاپ مرورگر از همان JSON)',
    ],
  },
  {
    // W2 (2026-11-04). Page ۱۸ of the standard's §۲ table. `config.` sits in مدیریت: it is a change
    // of configuration (and, through the matrix section, of law), not a read. The page asks for the
    // module key only — each section's own write key is enforced by the server before the first write.
    id: 'config',
    path: 'config',
    title: 'پشتیبان و بازیابی تنظیمات',
    crumb: 'پشتیبان تنظیمات',
    group: 'admin',
    icon: 'shield',
    capability: 'config.backup',
    status: 'live',
    planPage: 18,
    apiRefs: [
      'GET /ops/v1/config/export ✅ E1 (W2 — بخشی که فراخوان نمی‌تواند بخواند در omitted نام برده می‌شود؛ دریافت ممیزی می‌شود)',
      'POST /ops/v1/config:validate ✅ E1 (W2 — داوری با همان اعتبارسنج درِ هر بخش؛ هیچ نوشتنی)',
      'POST /ops/v1/config:apply ✅ E1 (W2 — همه یا هیچِ اعتبارسنجی، نقطهٔ بازگشت پیش از نوشتن؛ بدون scope ماشینی)',
      'GET /ops/v1/config/restore-points ✅ E1 (W2 — ده نقطهٔ آخر)',
      'GET /ops/v1/config/restore-points/{point_id} ✅ E1 (W2 — نقطه به‌صورت یک bundle عادی)',
    ],
  },
  {
    // W5b (2026-11-04). Page ۱۹ of the standard's §۲ table. `subscriber.` resolves to
    // زیرساخت by the longest-prefix rule (§۲), because this family is about what the network
    // gives a person — not about administering the ACS. Ten doors already existed (S1/S2/S3/S4)
    // with an empty console column; this page consumes them and opens no new one. The page
    // asks for the module key only: the mask is the server's, decided by the LEVEL on
    // `subscriber.read`, and every write names its own key.
    id: 'subscribers',
    path: 'subscribers',
    title: 'مشترکان',
    crumb: 'مشترکان',
    group: 'infrastructure',
    icon: 'search',
    capability: 'subscriber.read',
    status: 'live',
    planPage: 19,
    apiRefs: [
      'GET /ops/v1/subscribers ✅ E1 (S1 — فهرست و شمار پشت صفحه؛ ماسک سطح-وابسته سرور)',
      'POST /integration/v1/devices:search ✅ E1 (S2 — معیارها در بدنهٔ POST، هرگز در URL)',
      'GET /ops/v1/subscribers/{id} ✅ E1 (S1 — ردیف همانگونه که سرور داد)',
      'GET /ops/v1/subscribers/{id}/overview ✅ E1 (S3 — شش بخش؛ بخش غایب null است، نه صفر)',
      'POST …/{id}:suspend · :resume ✅ E1 (S4 — یک فیلد روی یک ردیف، کلید Idempotency الزامی)',
      'POST /ops/v1/subscribers ✅ E1 (S1 — هویت را در میسازد، کلید الزامی)',
      'GET /ops/v1/service-profiles ✅ E1 (S1 — پروفایل تجاری، بدون دادهٔ شخصی و بدون ماسک)',
      'POST /ops/v1/service-profiles ✅ E1 (کلید الزامی) · PUT …/{id} ✅ E1 (جایگزینی کامل، بیکلید)',
    ],
  },
  {
    id: 'devices',
    path: 'devices',
    title: 'موجودی دستگاه‌ها',
    crumb: 'موجودی',
    group: 'devices',
    icon: 'inventory',
    capability: 'fleet.devices.read',
    status: 'live',
    planPage: 2,
    apiRefs: ['GET /integration/v1/devices ✅'],
  },
  {
    id: 'device-detail',
    path: 'devices/:deviceId',
    title: 'جزئیات دستگاه',
    crumb: 'جزئیات دستگاه',
    group: 'devices',
    icon: 'device',
    capability: 'device.params.read',
    status: 'live',
    hidden: true,
    planPage: 3,
    apiRefs: [
      'GET /devices/{id} ✅',
      'GET /devices/{id}/parameters ✅',
      'GET /devices/{id}/stations ✅',
      'POST /devices/{id}/actions ✅',
      'GET /ops/v1/jobs?device ✅ (برگهٔ مشاغل از U-16-f این در را می‌خواند؛ type پیشوندِ action است)',
      'GET /ops/v1/devices/{id}/operations ✅ (U-16-f — شیارهای قفل هم در فهرست می‌مانند، قاعدهٔ B-۶)',
      'GET /ops/v1/devices/{id}/operations/{op}/value ✅ (U-16-f — منبعِ عدد در پاسخ است: snapshot/acs-direct/olt-snmp/none)',
      'PUT /ops/v1/devices/{id}/operations/{op} ✅ (U-16-f — 202 یعنی queued، هرگز اعمال‌شده)',
      'POST /ops/v1/devices/{id}/operations/{op}:run ✅ (U-16-f — تأیید تایپ‌شدهٔ خودِ در)',
      'PUT /ops/v1/devices/{id}/parameters/raw ✅ (U-16-f — کلید جدا: device.params.raw، اپراتور none)',
      'GET /ops/v1/audit ✅ E1 (برگهٔ ممیزیِ این دستگاه از U-16-d؛ فیلتر device_id همان تطابقِ کاملِ خودِ در است و ردیفِ بی‌device_id اینجا نمی‌آید)',
    ],
  },
  {
    id: 'activations',
    path: 'activations',
    title: 'فعال‌سازی (ZTP)',
    crumb: 'فعال‌سازی ZTP',
    group: 'operations',
    icon: 'activation',
    capability: 'activation.read',
    status: 'live',
    planPage: 4,
    apiRefs: ['POST /ops/v1/activations ✅', 'GET /ops/v1/activations/{id} ✅'],
  },
  {
    id: 'diagnostics',
    path: 'diagnostics',
    title: 'عیب‌یابی',
    crumb: 'عیب‌یابی',
    group: 'operations',
    icon: 'diagnostics',
    capability: 'diagnostics.run',
    status: 'live',
    planPage: 7,
    apiRefs: ['POST …/devices/{id}/diagnostics ✅', 'GET …/diagnostics/{job} ✅'],
  },
  {
    id: 'alarms',
    path: 'alarms',
    title: 'آلارم‌ها و رویدادها',
    crumb: 'آلارم‌ها',
    group: 'monitoring',
    icon: 'alarm',
    capability: 'alarm.read',
    status: 'live',
    planPage: 8,
    apiRefs: [
      'GET /ops/v1/alarms ✅',
      'POST /ops/v1/alarms/{event_id}:ack ✅',
      'POST /ops/v1/alarms/{event_id}:clear ✅ N2',
      'POST /ops/v1/alarms/{event_id}:escalate ✅ N2',
      'POST /ops/v1/alarms/{event_id}:assign ✅ N2',
      'GET /integration/v1/events ✅',
      'SSE live stream 📅',
    ],
  },
  {
    id: 'audit',
    path: 'audit',
    title: 'دفتر عملیات (audit)',
    crumb: 'دفتر عملیات',
    group: 'admin',
    icon: 'shield',
    capability: 'fleet.audit.read',
    status: 'live',
    planPage: 13,
    apiRefs: [
      'GET /ops/v1/audit ✅ (دفتر با کلید fleet.audit.read؛ صفحهٔ R7 از U-16-d ساخته شد — برای فراخوان machine همان `client_id` را می‌نویسد نه شخص، پس G-IDN-05 باز است)',
    ],
  },
  {
    id: 'keys',
    path: 'keys',
    title: 'کلیدهای امضای این استقرار',
    crumb: 'کلیدهای امضا',
    group: 'admin',
    icon: 'shield',
    capability: 'keys.read',
    status: 'live',
    // §۳'s numbered table has no page for this door — `docs/plans/2026-10-17-…` appendix **الف** does
    // (a live door with no UI). 13 keeps it beside the ledger in مدیریت (the §۲ prefix rule puts
    // `keys.read` there) without claiming a numbered plan page it does not have.
    planPage: 13,
    apiRefs: [
      'GET /ops/v1/keys ✅ (درِ U-08-b با کلید keys.read؛ برگهٔ کنسول از U-16-e1 ساخته شد — فقط metadata، و قلم پیوست الف «بی‌UI» بودنش را ثبت کرده بود. هیچ scope‌ای به keys.read نگاشت نشده، پس فراخوان machine هرگز بازش نمی‌کند و از چهار نقش انسانی تنها مالک می‌بیندش)',
    ],
  },
  {
    id: 'profile',
    path: 'profile',
    title: 'پروفایل عملیات',
    crumb: 'پروفایل عملیات',
    group: 'admin',
    icon: 'settings',
    capability: 'profile.read',
    status: 'live',
    // Like `keys`, this is appendix **الف**'s door-family, not a numbered plan page: §۲'s placement
    // rule does not enumerate a `profile.*` prefix at all, and the page joins the settings family in
    // مدیریت because `profile.read`/`profile.activate` are the settings family's neighbours in
    // `identity.py` (that file's own comment) and the write doors sit on the edit levels §۴ publishes.
    // U-16-e2 built the console half; the six ✅ below are the six doors of `routers/profile.py`.
    planPage: 13,
    apiRefs: [
      'GET /ops/v1/operator-profile ✅ (export عیناً از همان در؛ تنها قالب export — U-16-e2)',
      'POST /ops/v1/operator-profile:validate ✅ (همهٔ خطاها در یک گزارش؛ بی‌Idempotency-Key به‌عمد — U-16-e2)',
      'PUT /ops/v1/operator-profile ✅ (نسخهٔ immutable با CAS روی expected_version و کلید لازم؛ سطح edit کلید profile.activate — U-16-e2)',
      'GET /ops/v1/operator-profile/versions ✅ (پنجرهٔ کراندار، newest-first، بی‌سند در ردیف‌ها — U-16-e2)',
      'POST /ops/v1/operator-profile:rollback ✅ (فعال‌سازی پیشرؤ با confirm:true و کلید لازم — U-16-e2)',
      'GET /ops/v1/operator-profile/readiness ✅ (گزارش می‌دهد و تصمیم نمی‌گیرد؛ هرگز نوشتن مجاز نمی‌کند — U-16-e2)',
    ],
  },
  {
    // Page ۱۲ of the standard's §۲ table — the fleet job list. The door (`routers/system.py:177`)
    // has answered the fleet since T10c; what it never had was a console, which appendix **الف** of
    // `docs/plans/2026-10-17-u16-console-workflows-and-audit-actors.md` recorded. §۲'s prefix rule
    // (`jobs.*` → زیرساخت) owns the group, not taste.
    id: 'jobs',
    path: 'jobs',
    title: 'مشاغل سامانه',
    crumb: 'مشاغل',
    group: 'infrastructure',
    icon: 'clock',
    capability: 'jobs.read',
    status: 'live',
    planPage: 12,
    apiRefs: [
      'GET /ops/v1/jobs?device&status&type ✅ (type پیشوندِ خودِ action است، نه شمارنده؛ total شمارِ همین فیلتر است — U-16-f)',
    ],
  },
  {
    // Page ۱۱ of the standard's table. `pendingReason: 'api'` was stale (plan D8): every door this
    // page reads or writes has stood for a long time — the four user doors of `routers/identity.py`,
    // the two settings doors of `routers/system.py`, the session doors, and the ledger. What was
    // missing was the console: U-16-g1 built the human-management half and U-16-g2 built the matrix
    // editor — `policy.matrix` now has its UI, per-role only, gated on the effective mirror, with the
    // door's refusals rendered as arrived and the accepted write answered by its own ledger row.
    id: 'settings',
    path: 'settings',
    title: 'تنظیمات و کاربران',
    crumb: 'تنظیمات',
    group: 'admin',
    icon: 'settings',
    capability: 'settings.read',
    status: 'live',
    planPage: 11,
    apiRefs: [
      'POST /ops/v1/sessions ✅ (کنسول از همین در وارد می‌شود — U-08-f)',
      'CRUD /ops/v1/users ✅ R1 (برگهٔ کاربران از U-16-g1؛ 409 last-owner همان جملهٔ خودِ در است، نه پیشگیریِ UI)',
      'GET/PUT /ops/v1/settings ✅ (U-16-g1 — SETTINGS_INTENT_NOTE در پاسخ می‌ماند: نیت ثبت می‌شود، live اعمال نمی‌شود)',
      'GET/PUT /ops/v1/permissions ✅ (هر دو در پشت policy.matrix؛ ویرایشگر ماتریس از U-16-g2 — per-role، آینهٔ effective، جمله‌های §۳ خودِ در)',
      'GET /ops/v1/audit ✅ E1 (برگهٔ دفتر از U-16-d؛ کلیدش fleet.audit.read است نه settings.read)',
    ],
  },
  {
    id: 'olts',
    path: 'olts',
    title: 'OLTها',
    crumb: 'OLTها',
    group: 'infrastructure',
    icon: 'olt',
    capability: 'plant.read',
    status: 'live',
    planPage: 9,
    apiRefs: [
      'GET /ops/v1/olts ✅',
      'GET /ops/v1/olts/{olt_id}/onus ✅',
      'POST /ops/v1/olts/{olt_id}/onus:authorize ✅',
      'GET /ops/v1/devices/{id}/optical ✅',
      'GET …/onus/{sn}/detail ✅ E1 (W5e — unknown_fields adapter عیناً رندر می‌شود)',
      'GET …/onus/{sn}/mac-table ✅ E1 (W5e — خالی = «نیاموخته»، note حکم می‌کند)',
      'POST …/onus/{sn}:reboot ✅ E1 (W5e — reboot_issued حکم سرور است نه «ریستارت شد»)',
      'POST …/onus:bulk-authorize ✅ E1 (W5e — فهرست ترتیب‌دارِ هدف؛ SN تکراری 422 خود در)',
      'GET …/pon-ports ✅ E1 (W5e — شمار ONU هر پورت؛ صفر ≠ خوانده‌نشده)',
      'GET …/pon-capacity ✅',
    ],
  },
];

/** Sidebar order = plan order (pages 1..11), grouped by operator intent. */
export const NAV_PAGES: readonly NavPage[] = [...LIVE_PAGES, ...PENDING_PAGES];

/** Sidebar entries of a group, in plan order (page 1 … page 11). */
export function pagesInGroup(group: NavGroup): NavPage[] {
  return NAV_PAGES.filter((page) => page.group === group && !page.hidden).sort(
    (a, b) => a.planPage - b.planPage,
  );
}

export function pageById(id: string): NavPage | undefined {
  return NAV_PAGES.find((page) => page.id === id);
}

/**
 * The capability a page declares, by its nav id.
 *
 * Pages call this instead of writing the key inline, so the registry below stays the one place a
 * page's access key exists — and a page whose id is not in the nav has no capability to mirror,
 * which is a build error rather than a silently-permissive page.
 */
export function capabilityForPage(id: string): string {
  const page = pageById(id);
  if (!page) {
    throw new Error(
      `no navigation entry is registered for page id "${id}" — it cannot declare a capability`,
    );
  }
  return page.capability;
}

export function livePageIds(): string[] {
  return NAV_PAGES.filter((p) => p.status === 'live').map((p) => p.id);
}

export function pendingPageIds(): string[] {
  return NAV_PAGES.filter((p) => p.status === 'pending').map((p) => p.id);
}
